import React, { useEffect, useState } from "react";
import { RegisterOptions, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme, TextInput, Subheading, TouchableRipple } from "react-native-paper";
import { useSelector } from "react-redux";
import { ACCOUNT } from "app/shared/services/services-model.type";
import { useDesktopMediaQuery } from "@media-query";
import { Block } from "@block";
import { InputText } from "app/shared/components/form/input";
import { StackSpacing } from "@components/spacing";
import { formatNumberThousand } from "app/shared/util/format-currency";
import AccountSourceModal from "app/shared/components/modal/bene-account-source/accounts-source.modal";
import GlobalModal, { createGlobalModal, globalModal } from "app/shared/components/modal/global-modal/global-modal";
import { mapAccountPayments } from "app/shared/util/common";
import { getListAccountSelector } from "app/modules/account-management/reducer/selectors";
import { Constant } from "app/shared/constant/constant";

export interface SourceTermDepositTopUpProps {
  amountContent: JSX.Element;
  onChangeAccount: (accountDebit: ACCOUNT, isValid: boolean) => void;
  typeArray?: Array<string>;
  account?: ACCOUNT;
  isSubmit?: boolean;
  isAccountDefault?: boolean;
}

enum FormEnum {
  accountNo,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export interface SourceAccountData {
  accountNo: string;
}

const SourceTermDepositTopUp = (props: SourceTermDepositTopUpProps) => {
  const { typeArray, account, isAccountDefault = false, isSubmit, amountContent, onChangeAccount = () => {} } = props;
  const { colors, fonts } = useTheme();
  const isDesktop = useDesktopMediaQuery();
  const { t } = useTranslation();

  const listAccountStore = useSelector(getListAccountSelector);

  const [globalModalRef] = useState<React.RefObject<any>>(createGlobalModal());
  const [accountDebit, setAccountDebit] = useState<ACCOUNT>(account || {});
  const [listAccount, setListAccount] = useState<ACCOUNT[]>([]);

  const validateSchemeForm: ValidDataSchemeFormData = {
    accountNo: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_TAI_KHOAN"),
      },
      validate: {
        limitAmount: (value) => {
          if (value?.length) {
            if (accountDebit?.AVAILABLEBALANCE && Number(accountDebit.AVAILABLEBALANCE) <= 0) {
              return t("ACC_SO_DU_KHA_DUNG_NOT_ENOUGH").toString();
            }
          }
          return true;
        },
      },
    },
  };

  const textInputStyleCommonProps: object = {
    style: { color: colors.accent, backgroundColor: colors.backgroundForm, fontWeight: "700", textShadowColor: "black" },
    mode: "flat",
    outlineColor: "transparent",
    theme: {
      colors: {
        primary: colors.accent,
        text: colors.accent,
      },
    },
  };

  const handlerSelectAccount = (acc: ACCOUNT) => {
    globalModal.hide(globalModalRef);
    console.log("handlerSelectAccount: ", acc);
    setAccountDebit(acc);
  };

  const onPressChoose = () => {
    const content: JSX.Element = (
      <AccountSourceModal
        account={accountDebit}
        typeArray={typeArray}
        checkCurrentCode={true}
        onPressAcceptAccount={handlerSelectAccount}
      />
    );
    globalModal.show(content, globalModalRef);
  };

  const chooseAccount: JSX.Element = <TextInput.Icon name="chevron-down" onPress={onPressChoose} />;

  const { control, setValue, getValues, trigger } = useForm<SourceAccountData>({
    defaultValues: {
      accountNo: "",
    },
    mode: "onChange",
  });

  const getAccountDefault = React.useCallback(
    (list: ACCOUNT[]) => {
      if (!accountDebit?.ACCOUNTNO && list && list.length) {
        let accountDefault = list[0];
        const availAbleBalance = Number(accountDefault?.AVAILABLEBALANCE || 0);
        if ((accountDefault.STATUS !== "A" || availAbleBalance === 0) && list.length > 1) {
          const accountS = list.filter((item) => {
            const amount = Number(item?.AVAILABLEBALANCE || 0);
            if (typeArray?.length && typeArray.length === 1 && typeArray[0] === Constant.ACCOUNTTYPE.ACCOUNT_PAYMENT) {
              return item.STATUS === "A" && amount > 0 && item.PRODUCTCODE === Constant.PRODUCT_CODE_PAYMENT;
            }
            return item.STATUS === "A" && amount > 0;
          });
          if (accountS && accountS.length) accountDefault = accountS[0];
        }
        return accountDefault;
      }
      return accountDebit;
    },
    [accountDebit, typeArray],
  );

  const getListAccount = React.useCallback(
    (data?: ACCOUNT[]) => {
      let list: ACCOUNT[] = [];
      if (data?.length) {
        const filter = data.filter((item) => {
          if (typeArray?.length) {
            return item.STATUS === "A" && item.ACCOUNTTYPE?.length && typeArray.includes(item.ACCOUNTTYPE) && item.CURRENCYCODE === "VND";
          } else {
            return item.STATUS === "A" && item.CURRENCYCODE === "VND";
          }
        });
        list = filter;
      }
      return mapAccountPayments(list);
    },
    [typeArray],
  );

  const onChange = React.useCallback(
    async (accountNew: ACCOUNT) => {
      if (accountNew?.ACCOUNTNO?.length && accountNew.ACCOUNTNO !== getValues("accountNo")) {
        setValue("accountNo", accountNew.ACCOUNTNO);
        const accountTrigger = await trigger("accountNo");
        onChangeAccount(accountNew, accountTrigger);
      }
    },
    [getValues, setValue, trigger, onChangeAccount],
  );

  useEffect(() => {
    if (listAccountStore?.ACCOUNT.length) {
      const acc = getListAccount(listAccountStore.ACCOUNT);
      setListAccount(acc);
    }
  }, [getListAccount, listAccountStore]);

  useEffect(() => {
    if (listAccount.length && !accountDebit.ACCOUNTNO?.length) {
      const accountNew = getAccountDefault(listAccount);
      setAccountDebit(accountNew);
    }
  }, [getAccountDefault, listAccount, accountDebit]);

  useEffect(() => {
    if (isAccountDefault && account?.ACCOUNTNO?.length && accountDebit?.ACCOUNTNO) {
      setAccountDebit(account);
    }
  }, [account, accountDebit, isAccountDefault]);

  useEffect(() => {
    onChange(accountDebit);
  }, [onChange, accountDebit]);

  useEffect(() => {
    if (isSubmit) {
      trigger("accountNo");
    }
  }, [isSubmit, trigger]);

  const rightAmount = <TextInput.Affix textStyle={fonts.regular} text={t("COMMON_VND")} />;

  return (
    <Block pt={16} row={isDesktop ? true : false}>
      <Block w={200}>
        <StackSpacing size="medium" />
        <Subheading style={{ ...fonts.semibold }} numberOfLines={1}>
          {t("SAVING_THONG_TIN_GIAO_DICH")}
        </Subheading>
        <StackSpacing size="medium" />
      </Block>
      <Block flex={1} bg={colors.backgroundForm} br={12} p={16}>
        <TouchableRipple onPress={onPressChoose}>
          <InputText
            rules={validateSchemeForm.accountNo}
            name="accountNo"
            control={control}
            inputProps={{ editable: false, right: chooseAccount }}
            label={t("SAVING_TAI_KHOAN_TRICH_NO")}
          />
        </TouchableRipple>

        <TextInput
          {...textInputStyleCommonProps}
          label={t("ACC_SO_DU_KHA_DUNG")}
          right={rightAmount}
          editable={false}
          value={formatNumberThousand(accountDebit.AVAILABLEBALANCE)}
        />
        {amountContent}
      </Block>
      <GlobalModal ref={globalModalRef} />
    </Block>
  );
};

export default SourceTermDepositTopUp;
